import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatQty, sumField, splitRecordsByStatus, buildCancelledSummaryCard } from "../../utils/listSummaryUtils";
import moment from "moment";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllWorkSchedules, deleteWorkSchedule, } from "../../services/WorkScheduleServices";
import { displayError } from "../../helpers";
import { buildWorkScheduleQueryParams } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { bootstrapListViewLoad } from "../../utils/listViewBootstrap";

function WorkScheduleTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [listViewConfig, setListViewConfig] = useState(null);
    const right_pinned = ["id"];
    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayRecords = activeRecords.filter((row) => row.schedule_date === todayDateLabel);
        return buildGradientCard('Today Sch.', formatQty(sumField(todayRecords, 'scheduled_qty')), 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Scheduled', formatQty(sumField(activeRecords, 'scheduled_qty')), 'blue'),
            buildGradientCard('Delivered', formatQty(sumField(activeRecords, 'supplied_qty')), 'purple'),
            buildGradientCard('Accepted', formatQty(sumField(activeRecords, 'accepted_qty')), 'teal'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            valueField: 'scheduled_qty',
            entityLabel: 'WS cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Work Schedules (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled work schedules in the current list.',
        columns: [
            { key: 'schedule_no', label: 'WS No', align: 'center' },
            { key: 'schedule_date', label: 'Date', align: 'center' },
            { key: 'customer', label: 'Customer', align: 'left' },
            { key: 'consignee', label: 'Site', align: 'left' },
            { key: 'scheduled_qty', label: 'Sch Qty', align: 'right' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            schedule_no: row.schedule_no,
            schedule_date: row.schedule_date,
            customer: row.customer,
            consignee: row.consignee,
            scheduled_qty: formatQty(row.scheduled_qty),
        }),
        footerRowBuilder: (rows) => ({
            schedule_no: 'Total',
            schedule_date: '',
            customer: '',
            consignee: `${rows.length} WS`,
            scheduled_qty: formatQty(sumField(rows, 'scheduled_qty')),
        }),
    }), [cancelledRecords]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'plant', 
                headerName: 'Plant'
            },
            {
                fieldName: 'schedule_no', 
                headerName: 'Job No'
            },
           
            {
                fieldName: "schedule_date",
                headerName: 'Job Date'
            },
            {
                fieldName: "order_no",
                headerName: 'SalesOrder No'
            },
            {
                fieldName: "order_date",
                headerName: "SalesOrder Date"
            },
            {
                fieldName: "customer",
                headerName: "Customer"
            },
            {
                fieldName: "consignee",
                headerName: "Site / Project"
            },
            {
                fieldName: "salesrep",
                headerName: "Marketing Name"
            },
            {
                fieldName: "order_quantity",
                headerName: "Order Qty"
            },
            {
                fieldName: "scheduled_qty",
                headerName: "Scheduled Qty"
            },
            {
                fieldName: "supplied_qty",
                headerName: "Delivered Qty"
            },
            {
                fieldName: "accepted_qty",
                headerName: "Accepted Qty"
            },
            {
                fieldName: "balance_qty",
                headerName: "Balanced Qty"
            },
            {
                fieldName: "delivery_mode",
                headerName: "Mode Of Delivery"
            },
            {
                fieldName: "pump",
                headerName: "Pump No"
            },
            {
                fieldName: "status",
                headerName: "Status"
            },
            {
                fieldName: "status_2",
                headerName: "Schedule Status"
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
       
        ],
        [],);

    const onEditWorkSchedule = (workscheduleId, event) => {
       navigate(`/WorkScheduleEdit/${workscheduleId}/`);
    };

    const onPrintJobCompletionReport = (workscheduleId, event) => {
        event.preventDefault();
        navigate(`/JobCompletionReport/${workscheduleId}/`);
    };

    const onDeleteWorkSchedule = (workscheduleId, event) => {
        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            deleteWorkSchedule(cookies,workscheduleId)
            .then(response => {
                setRefreshKey(oldKey => oldKey +1);
                Swal.fire('Deleted Successfully!', '', 'success');
            })
            .catch(error => {
                setRefreshKey(oldKey => oldKey +1);
                displayError(error.response.data,'Delete Failed');
            }); 
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
    };

    const handleCSVExport = (event) => {
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(ws => ({
                plant:ws.plant,
                schedule_no: ws.schedule_no,
                schedule_date: ws.schedule_date,
                scheduled_qty:ws.scheduled_qty,
                order_quantity:ws.order_quantity,
                supplied_qty:ws.supplied_qty,
                accepted_qty:ws.accepted_qty,
                balance_qty:ws.balance_qty,
                delivery_mode:ws.delivery_mode.toUpperCase(),
                order_no:ws.order_no,
                order_date:ws.order_date,
                consignee:ws.consignee,
                pump:ws.pump,
                customer:ws.customer,
                status:ws.status,
                salesrep:ws.salesrep
            })));
    };

    const handlePDFExport = (event) => {
        exportAsPDF(
            'List of WorkShedule Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(ws => ({
                plant:ws.plant,
                schedule_no: ws.schedule_no,
                schedule_date: ws.schedule_date,
                scheduled_qty:ws.scheduled_qty,
                supplied_qty:ws.supplied_qty,
                accepted_qty:ws.accepted_qty,
                order_quantity:ws.order_quantity,
                balance_qty:ws.balance_qty,
                delivery_mode:ws.delivery_mode.toUpperCase(),
                order_no:ws.order_no,
                order_date:ws.order_date,
                customer:ws.customer,
                consignee:ws.consignee,
                pump:ws.pump,
                status:ws.status,
                salesrep:ws.salesrep
            })),
            'WorkScheduleList.pdf'
        );
    };

    const loadWorkScheduleList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('work_schedule');
        setIsLoading(true);
        return getAllWorkSchedules(cookies, {
            ...buildWorkScheduleQueryParams(activeConfig),
            limit: 500,
        })
        .then( workscheduleList => {
            const tableData = (workscheduleList?.work_schedule_list || [])
            .map(ws => {
                const scheduledQty = Number(ws.scheduled_qty) || 0;
                const suppliedQty = Number(ws.supplied_qty) || 0;
                const orderQty = Number(ws.sales_order_detail?.order_quantity) || 0;
                const isFormallyCompleted = ws.status === 'completed' || Boolean(ws.is_schedule_completed);
                const isQtyComplete = suppliedQty > 0 && (
                    (scheduledQty > 0 && suppliedQty >= scheduledQty) ||
                    (orderQty > 0 && suppliedQty >= orderQty)
                );
                const canPrintJobCompletion = isFormallyCompleted || isQtyComplete;

                return {
                id :ws.id,
                plant: ws.plant?.alias_name ?? '',
                schedule_no: ws.schedule_no,
                schedule_date: ws.schedule_date,
                scheduled_qty:ws.scheduled_qty,
                accepted_qty:ws.accepted_qty ?? 0,
                supplied_qty:ws.supplied_qty ?? 0,
                order_quantity:ws.sales_order_detail?.order_quantity ?? 0,
                balance_qty:ws.balance_qty,
                delivery_mode:(ws.delivery_mode ?? '').toUpperCase(),
                order_no:ws.sales_order_detail?.order_no ?? '',
                order_date:ws.sales_order_detail?.order_date ?? '',
                consignee:ws.sales_order_detail?.consignee?.project_name ?? '',
                customer:ws.sales_order_detail?.consignee?.customer?.name ?? '',
                salesrep:ws.sales_order_detail?.consignee?.customer?.salesrep?.name ?? '',
                pump:ws.pump?.name ?? '',
                status:ws.status,
				status_2:ws.status_2?'Active':'Cancelled',
                is_schedule_completed:ws.is_schedule_completed?'Yes':'No',
                hide_row_edit:ws.is_schedule_completed?'Yes':'No',
                hide_row_print: canPrintJobCompletion ? 'No' : 'Yes',
            };
            });

            setData({
                total: tableData.length,
                records: tableData
            });
        })
        .catch((error) => {
            setData({ total: 0, records: [] });
            displayError(error.response?.data ?? error.message, 'Failed to load work schedules');
        })
        .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        setListViewConfig(config);
        loadWorkScheduleList(config);
    }, [loadWorkScheduleList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'work_schedule',
            loadFn: async (config) => {
                setListViewConfig(config);
                await loadWorkScheduleList(config);
            },
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadWorkScheduleList, refreshKey]);
    
    return (
        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader
                module="PLANNING"
                title="List of Schedules"
                viewKey="work_schedule"
                onSettingsSaved={handleSettingsSaved}
                formLink="/WorkSchedule"
                addNewLabel="New Schedule"
                onExportCSV={handleCSVExport}
                onExportPDF={handlePDFExport}
            />
                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />
                <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='LIST OF SCHEDULES'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditWorkSchedule}
                            onRowDelete={onDeleteWorkSchedule}
                            onRowPrint={onPrintJobCompletionReport}
                            hidePrint={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            right_pinned={right_pinned}
                        />
                </div>
                </div> 
            </div>
</div>
    );
}

export default WorkScheduleTable;
